import { useState, useEffect } from "react";
import { User, Building2, Briefcase, Wallet, Users, Target, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../../api";
import MiniLoader from "../../../components/MiniLoader";

export default function Organigramme({ type, rootId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [type, rootId]);

  const fetchData = async () => {
    try {
      if (type === "Famille") {
        const fam = await api.get(`/familles/${rootId}/tree`);
        setData(fam.data);
      } else {
        const soc = await api.get(`/societes/${rootId}/tree`);
        setData(soc.data);
      }
    } catch (err) {
      console.error("Error fetching organigramme", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <MiniLoader />;
  if (!data) return <div className="p-8 text-center text-subtle">Aucune donnée disponible.</div>;

  return (
    <div className="w-full h-full min-h-[400px] bg-workspace/50 rounded-xl p-6 overflow-auto">
      <div className="flex flex-col items-center">
        
        {/* Root Node */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface border border-info-200 dark:border-info-900/50 shadow-hp-md rounded-xl p-4 w-72 flex flex-col items-center relative z-10"
        >
          <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mb-3">
            {type === "Famille" ? <Users className="w-6 h-6 text-info-600" /> : <Building2 className="w-6 h-6 text-info-600" />}
          </div>
          <h3 className="text-lg font-bold text-primary text-center">
            {type === "Famille" ? data.nomFamille : data.denomination}
          </h3>
          <p className="text-sm text-subtle mt-1">{type === "Famille" ? "Foyer Fiscal" : "Société Tête de Pont"}</p>
          
          {(data.patrimoineNet > 0 || data.valorisationEstimee > 0) && (
            <div className="mt-3 px-3 py-1 bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400 font-semibold text-sm rounded-full">
              {data.patrimoineNet ? `${(data.patrimoineNet / 1000).toFixed(0)} k€ net` : `${(data.valorisationEstimee / 1000).toFixed(0)} k€ val.`}
            </div>
          )}
        </motion.div>

        {/* Children connections */}
        {data.personnes && data.personnes.length > 0 && (
          <div className="flex flex-col items-center w-full mt-8 relative">
            <div className="absolute top-[-32px] w-[2px] h-8 bg-workspace" />
            <div className="absolute top-0 w-3/4 h-[2px] bg-workspace" />
            
            <div className="flex justify-around w-full mt-4 gap-6">
              {data.personnes.map((personne, i) => (
                <div key={i} className="flex flex-col items-center relative">
                  <div className="absolute top-[-16px] w-[2px] h-4 bg-workspace" />
                  
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 * i }}
                    className="bg-surface border border-workspace shadow-sm rounded-xl p-4 w-64 flex flex-col items-center"
                  >
                    <div className="w-10 h-10 bg-info-50 dark:bg-info-900/20 rounded-full flex items-center justify-center mb-2">
                      <User className="w-5 h-5 text-info-500" />
                    </div>
                    <h4 className="font-semibold text-primary">{personne.prenom} {personne.nom}</h4>
                    <p className="text-xs text-subtle">{personne.roleFamille || "Membre"}</p>
                  </motion.div>

                  {/* Portefeuilles & Participations under Personne */}
                  {(personne.portefeuilles?.length > 0 || personne.participations?.length > 0) && (
                    <div className="flex flex-col items-center mt-6 relative w-full">
                      <div className="absolute top-[-24px] w-[2px] h-6 bg-workspace" />
                      
                      <div className="flex flex-col gap-3 w-full max-w-[240px]">
                        {personne.portefeuilles?.map((pf, j) => (
                          <motion.div 
                            key={`pf-${j}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 + (j * 0.1) }}
                            className="bg-surface-muted/50 border border-workspace rounded-lg p-3 flex items-center gap-3 w-full hover:border-info-300 transition-colors cursor-pointer"
                          >
                            <Wallet className="w-4 h-4 text-success-500 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-primary truncate">{pf.nom}</p>
                              <p className="text-xs text-subtle">{pf.type} • {pf.valorisation?.toLocaleString()} €</p>
                            </div>
                          </motion.div>
                        ))}

                        {personne.participations?.map((part, j) => (
                          <motion.div 
                            key={`part-${j}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3 + (j * 0.1) }}
                            className="bg-surface-muted/50 border border-workspace rounded-lg p-3 flex items-center gap-3 w-full hover:border-info-300 transition-colors cursor-pointer"
                          >
                            <Briefcase className="w-4 h-4 text-orange-500 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-primary truncate">{part.societe?.denomination}</p>
                              <p className="text-xs text-subtle">{part.pourcentageCapital}% • {part.valeurEstimee?.toLocaleString()} €</p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
